import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import { Loader2, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { DataGridToolbar } from './DataGridToolbar';
import { ColumnFilter } from './ColumnFilter';
import { PaginationBar } from './PaginationBar';
import { useInfiniteScroll } from './useInfiniteScroll';
import type { DataGridProps } from './types';

function StateRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="h-40 text-center text-sm text-muted-foreground">
        {children}
      </TableCell>
    </TableRow>
  );
}

function SkeletonRows({ rows, cols }: { rows: number; cols: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <TableRow key={r}>
          {Array.from({ length: cols }).map((_unused, c) => (
            <TableCell key={c}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function ErrorState({ onRetry }: { onRetry?: (() => void) | undefined }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <span>Something went wrong.</span>
      {onRetry && (
        <Button size="sm" variant="outline" onClick={onRetry}>
          Retry
        </Button>
      )}
    </div>
  );
}

export function DataGrid<T>(props: DataGridProps<T>) {
  const {
    rows,
    columns,
    onRowClick,
    getRowId,
    isLoading,
    isError,
    isFetchingNextPage,
    endReached,
    onLoadMore,
    emptyMessage = 'No results',
    filterGroups,
    selectedFilters,
    onFiltersChange,
    onSortChange,
    sortableColumns,
    sortState,
    page,
    totalPage,
    onPageChange,
    isFetching,
    rowSelection,
  } = props;

  // Numbered paging when the caller wires it up; otherwise the legacy scroll sentinel.
  const usePager = !!onPageChange;
  const selectionEnabled = !!rowSelection && !!getRowId;

  const pageRowIds = useMemo(
    () => (selectionEnabled && getRowId ? rows.map((row) => getRowId(row)) : []),
    [selectionEnabled, getRowId, rows],
  );
  const selectedOnPage = pageRowIds.filter((id) => rowSelection?.selectedIds.has(id));
  const allPageSelected = pageRowIds.length > 0 && selectedOnPage.length === pageRowIds.length;
  const somePageSelected = selectedOnPage.length > 0 && !allPageSelected;

  function toggleRow(id: string, checked: boolean) {
    if (!rowSelection) return;
    const next = new Set(rowSelection.selectedIds);
    if (checked) next.add(id);
    else next.delete(id);
    rowSelection.onChange(next);
  }

  function togglePage(checked: boolean) {
    if (!rowSelection) return;
    const next = new Set(rowSelection.selectedIds);
    for (const id of pageRowIds) {
      if (checked) next.add(id);
      else next.delete(id);
    }
    rowSelection.onChange(next);
  }

  const table = useReactTable<T>({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row, index) => (getRowId ? getRowId(row) : String(index)), // stable keys -> no dup-key bug
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  });

  // IntersectionObserver sentinel fires at ~80% — parity BaseListScreen.kt:89-103.
  const sentinelRef = useInfiniteScroll({
    onLoadMore,
    disabled: usePager || endReached || isLoading || isFetchingNextPage,
  });

  const colSpan = columns.length + (selectionEnabled ? 1 : 0);

  return (
    <div className="flex h-full flex-col rounded-xl border">
      <DataGridToolbar {...props} />

      {/* The ONE scroll container. `[&>div]:overflow-visible` neutralises the wrapper div that the
          shadcn <Table> emits (ui/table.tsx) — that wrapper is `overflow-auto`, which made it the
          nearest scrollport for the sticky <thead>, so the header scrolled away with the rows.
          Horizontal overflow simply moves up to this element, which is better anyway: the stuck
          header now scrolls sideways in sync with the body. */}
      <div className="relative flex-1 overflow-auto [&>div]:overflow-visible">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-muted">
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {selectionEnabled && (
                  <TableHead className="w-10 bg-muted shadow-[inset_0_-1px_0_var(--border)]">
                    <Checkbox
                      checked={allPageSelected ? true : somePageSelected ? 'indeterminate' : false}
                      onCheckedChange={(v) => togglePage(v === true)}
                      aria-label="Select all on this page"
                      disabled={pageRowIds.length === 0}
                    />
                  </TableHead>
                )}
                {hg.headers.map((header) => {
                  const columnId = header.column.id;
                  const groupDef = filterGroups?.find((g) => g.columnKey === columnId);
                  const sortable = !!onSortChange && !!sortableColumns?.includes(columnId);
                  const activeDir =
                    sortState && sortState.columnKey === columnId ? sortState.dir : null;
                  return (
                    <TableHead
                      key={header.id}
                      onClick={sortable ? () => onSortChange?.(columnId) : undefined}
                      // Background on the cell as well as the row so no row bleeds through the
                      // stuck header, and the bottom rule is an inset shadow because Tailwind's
                      // preflight sets border-collapse:collapse, under which Chrome drops the
                      // <tr> border once the thead is stuck.
                      className={cn(
                        'bg-muted shadow-[inset_0_-1px_0_var(--border)]',
                        sortable && 'cursor-pointer select-none',
                      )}
                    >
                      <div className="flex items-center gap-1">
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                        {sortable &&
                          (activeDir === 'asc' ? (
                            <ChevronUp className="h-3.5 w-3.5" />
                          ) : activeDir === 'desc' ? (
                            <ChevronDown className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground/40" />
                          ))}
                        {groupDef && (
                          <ColumnFilter
                            group={groupDef}
                            selected={selectedFilters?.[groupDef.columnKey] ?? []}
                            onChange={(vals) =>
                              onFiltersChange?.({ ...selectedFilters, [groupDef.columnKey]: vals })
                            }
                          />
                        )}
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {isError ? (
              <StateRow colSpan={colSpan}>
                <ErrorState onRetry={props.onRetry} />
              </StateRow>
            ) : isLoading ? (
              <SkeletonRows rows={8} cols={colSpan} />
            ) : rows.length === 0 ? (
              <StateRow colSpan={colSpan}>{emptyMessage}</StateRow>
            ) : (
              table.getRowModel().rows.map((row, i) => {
                const rowId = getRowId ? getRowId(row.original) : row.id;
                const selected = selectionEnabled && rowSelection.selectedIds.has(rowId);
                return (
                  <TableRow
                    key={row.id}
                    data-even={i % 2 === 0}
                    data-selected={selected || undefined}
                    onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                    className={cn(
                      'cursor-pointer data-[even=true]:bg-muted/30 hover:bg-muted/50',
                      selected && 'bg-primary/10 data-[even=true]:bg-primary/10 hover:bg-primary/15',
                    )}
                  >
                    {selectionEnabled && (
                      <TableCell
                        className="w-10"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={selected}
                          onCheckedChange={(v) => toggleRow(rowId, v === true)}
                          aria-label={`Select row ${rowId}`}
                        />
                      </TableCell>
                    )}
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {/* load-more sentinel + footer spinner (parity BaseListScreen.kt:116-132) */}
        {!usePager && !endReached && !isError && rows.length > 0 && (
          <div ref={sentinelRef} className="flex h-16 items-center justify-center">
            {isFetchingNextPage && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
          </div>
        )}
      </div>

      {usePager && !isError && (
        <PaginationBar
          page={page ?? 1}
          totalPage={totalPage ?? 0}
          onPageChange={onPageChange}
          {...(isFetching !== undefined ? { isFetching } : {})}
        />
      )}
    </div>
  );
}
